import { useEffect, useState } from "react";
import clsx from "clsx";
import lodash, { debounce } from "lodash";
import { v4 as uuidv4 } from "uuid";
import { useDispatch } from "react-redux";
import { AppBar, Drawer } from "@mui/material";
import { Guid } from "guid-typescript";

// COMPONENTS
import KogModalSideComponent from "@/main/components/modal/ModalSide";
import { IPartsCard, ISupplierCard } from "@/main/components/shared/card/cardShopping";

// SERVICES / REDUX
import { Cart, CartPart, addCart, updateCart, useCartReducer } from "@/main/store/cart";
import { partApi } from "@/main/api";

// SCREENS/COMPONENTS/CONTEXT LOCAL
import { OrderStepsEnum } from "@/main/services/enum/OrderStepsEnum";
import { ICategory } from "@/main/components/shared/asideFilter/optionCategory/categoryOption";
import AsideFilter from "@/main/components/shared/asideFilter/asideFilter";
import { useCreateOrderContext } from "../../context";
import ScreenParts, { INITIAL_PARTS_GRID_STATE, IPartsGridData } from "./screenParts/screenParts";
import ModalPartDetails from "./modalPartDetails/modalPartDetails";
import ModalCart from "./modalCart/modalCart";
import ScreenPartsSupplier from "./screenPartsSupplier/screenPartsSupplier";
import HeaderSearch from "./headerSearch/headerSearch";
import * as S from "./styles";
import { PaginationEnum } from "@/main/services/enum/PaginationEnum";

export interface IDetailSupplier {
	guid: string | null;
	supplierInventoryGuid: boolean;
}

const INITIAL_DETAIL_SUPPLIER = {
	guid: null,
	supplierInventoryGuid: false,
};

const INITIAL_FORM_FILTERS = {
	idsPartType: [] as string[],
	guidPartCategory: null as string | null,
	guidPartSubCategory: null as string | null,
	idsDamageArea: [] as number[],
	searchTerm: "",
	pageIndex: PaginationEnum.PageIndexIntial,
	pageSize: PaginationEnum.PageSize20,
};
const SEARCH_TIME_DEBOUNCE = 600;
let isResetFiltersGlobal = false;

function StepTwoScreen() {
	// hooks
	const cartReducer = useCartReducer();
	const dispatch = useDispatch();
	const classes = S.useStyles();
	const {
		savedData,
		guidQuote,
		handleCompletedStep,
		handleSaveStepData,
		showSupplierParts,
		handleContinueShopping,
		displaySupplierParts,
		isCompareSupplier,
		isSeeSuppliersAvailable,
	} = useCreateOrderContext();
	const findDataCurrentStep = savedData.find((item) => item.idStep === OrderStepsEnum.Parts);

	// Methods Initial Load State
	const loadOptionsSelectedDamageArea = () => {
		const dataStepOne = savedData.find((item) => item.idStep === OrderStepsEnum.VehicleInformation);
		if (dataStepOne?.data?.selectedDamageArea && findDataCurrentStep?.data === null) {
			return dataStepOne?.data?.selectedDamageArea;
		}

		const optionsSelected = findDataCurrentStep?.data?.selectedDamageArea || [];
		return optionsSelected;
	};
	// States
	const [openFilter, setOpenFilter] = useState(false);
	const [openModalDetails, setOpenModalDetails] = useState(false);
	const [openModalCart, setOpenModalCart] = useState(false);

	const [selectedParts, setSelectedParts] = useState<string[]>(findDataCurrentStep?.data?.selectedParts || []);
	const [selectedCategory, setSelectedCategory] = useState<ICategory | null>(
		findDataCurrentStep?.data?.selectedCategory || null,
	);
	const [selectedDamageArea, setSelectedDamageArea] = useState<string[]>(loadOptionsSelectedDamageArea());
	const [isResetFilters, setIsResetFilters] = useState(false);
	const [detailSupplier, setDetailSupplier] = useState<IDetailSupplier>(INITIAL_DETAIL_SUPPLIER);

	const [partsGridData, setPartsGridData] = useState<IPartsGridData>(INITIAL_PARTS_GRID_STATE);
	const [formFilters, setFormFilters] = useState(INITIAL_FORM_FILTERS);

	const handleSearchValue = debounce((value: string) => {
		loadListParts(value);
	}, SEARCH_TIME_DEBOUNCE);

	// Async Functions
	async function loadListParts(searchValue: string | null = null) {
		if (!showSupplierParts) {
			const newFormFilters = lodash.cloneDeep(formFilters);
			const isSubCategory = !selectedCategory?.subCategories;

			setPartsGridData({
				isLoading: true,
				totalItems: 0,
				data: [],
			});

			if (isResetFiltersGlobal) {
				newFormFilters.idsDamageArea = [];
				newFormFilters.guidPartCategory = null;
				newFormFilters.guidPartSubCategory = null;
				newFormFilters.searchTerm = "";

				isResetFiltersGlobal = false;
			} else {
				newFormFilters.idsDamageArea = selectedDamageArea.map((item) => Number(item));
				newFormFilters.guidPartCategory = null;
				newFormFilters.guidPartSubCategory = null;

				if (isSubCategory) {
					newFormFilters.guidPartSubCategory = selectedCategory ? String(selectedCategory?.key) : null;
				} else {
					newFormFilters.guidPartCategory = selectedCategory ? String(selectedCategory?.key) : null;
				}

				if (searchValue !== null) {
					newFormFilters.searchTerm = searchValue;
				}
			}

			partApi
				.getSearchParts(newFormFilters)
				.then((response) => {
					setPartsGridData({
						isLoading: false,
						totalItems: response.data.totalNumberOfRecords,
						data: response.data.totalNumberOfRecords > 0 ? response.data.results : [],
					});
				})
				.catch(() => {})
				.finally(() => {});
			setFormFilters(newFormFilters);
		}
	}

	// Handle Functions
	const handleDrawerOpen = () => setOpenFilter(true);
	const handleDrawerClose = () => setOpenFilter(false);

	useEffect(() => {
		const dataCart = cartReducer.cartItems.data;
		const dataNoSupplierAssigned = cartReducer.cartItems.dataNoSupplierAssigned;

		let isCompletedStep = false;
		if ((dataCart && dataCart.length > 0) || (dataNoSupplierAssigned && dataNoSupplierAssigned !== null)) {
			isCompletedStep = true;
		}

		handleSaveStepData(OrderStepsEnum.Summary, isCompletedStep, savedData);
	}, [cartReducer]);

	function handleAddCart(part: IPartsCard, supplier: ISupplierCard, quantityUpdate: number | null = null) {
		const idUnique = uuidv4();
		const hasExistsSupplierInCart = cartReducer.cartItems.data.find((item) => item.guid === supplier.guid);

		const formattedPart: CartPart = {
			id: idUnique,
			guid: part.guidQuoteSupplierItem,
			guidPart: part.guidPart,
			idPartType: part.idPartType || 0,
			quantity: 1,
			guidSupplierInventoryWarehouse: part.guidSupplierInventoryWarehouse ? part.guidSupplierInventoryWarehouse : "",
			guidQuoteSupplierItem: part.guidQuoteSupplierItem ? part.guidQuoteSupplierItem : "",
			deliveryBySupplier: part.deliveryBySupplier,
			externalPartId: part.externalPartId,
		};

		if (hasExistsSupplierInCart) {
			const hasExistsPartInCart = hasExistsSupplierInCart.parts.find(
				(item) =>
					item.guidQuoteSupplierItem === formattedPart.guidQuoteSupplierItem &&
					item.idPartType === formattedPart.idPartType,
			);

			if (hasExistsPartInCart) {
				const updatedPart = {
					...hasExistsPartInCart,
					quantity: quantityUpdate || (hasExistsPartInCart?.quantity || 0) + 1,
				};

				dispatch(updateCart(updatedPart, supplier.guid));
				return;
			}
		}

		const formattedCart: Cart = {
			id: idUnique,
			shipingCompany: {
				quotationShipingCompanyItems: [],
			},
			guid: supplier.guid,
			guidQuote: String(guidQuote),
			name: supplier.name,
			parts: [formattedPart],
			totalPartsPrice: formattedPart.price,
			totalShippingCost: formattedPart.shippingCost,
			guidSupplierInventoryWarehouse: formattedPart.guidSupplierInventoryWarehouse,
			totalParts: 1,
		};
		dispatch(addCart(formattedCart));
	}

	function handleSelectPart(idCheckbox: string) {
		const cloneOptionsSelected = lodash.cloneDeep(selectedParts);
		const index = cloneOptionsSelected.indexOf(idCheckbox);

		if (index > -1) {
			cloneOptionsSelected.splice(index, 1);
		} else {
			cloneOptionsSelected.push(idCheckbox);
		}

		setSelectedParts(cloneOptionsSelected);
	}

	function handleSelectDamageArea(idCheckbox: string) {
		const cloneOptionsSelected = lodash.cloneDeep(selectedDamageArea);
		const index = cloneOptionsSelected.indexOf(idCheckbox);

		if (index > -1) {
			cloneOptionsSelected.splice(index, 1);
		} else {
			cloneOptionsSelected.push(idCheckbox);
		}

		setSelectedDamageArea(cloneOptionsSelected);
	}

	function handleSelectCategory(categorySelected: ICategory | null) {
		setSelectedCategory(categorySelected);
	}

	function handleFinishOrder() {
		handleCompletedStep();
		handleSaveStepData(OrderStepsEnum.Summary, true, savedData);
	}

	function handleClearFilters() {
		setSelectedDamageArea([]);
		setSelectedParts([]);
		setSelectedCategory(null);
		handleSearchValue("");
		setIsResetFilters(true);
		isResetFiltersGlobal = true;
	}

	// Display functions
	function displayPartsDetails(supplierInventoryGuid: string, guidQuoteSupplierItem: string) {
		setOpenModalDetails(true);
		const request: IDetailSupplier = {
			guid: supplierInventoryGuid !== Guid.EMPTY ? supplierInventoryGuid : guidQuoteSupplierItem,
			supplierInventoryGuid: supplierInventoryGuid !== Guid.EMPTY ? false : true,
		};
		setDetailSupplier(request);
	}

	function displayCart() {
		setOpenModalCart(true);
	}

	useEffect(() => {
		if (!isResetFilters && !showSupplierParts) {
			loadListParts();
		} else {
			setIsResetFilters(false);
		}
		// Save data from this step when unmount
		return () => {
			const savedStepData = {
				selectedParts,
				selectedDamageArea,
				selectedCategory,
				formFilters,
			};

			handleSaveStepData(OrderStepsEnum.Parts, true, savedStepData);
		};
	}, [selectedParts, selectedDamageArea, selectedCategory, setIsResetFilters]);

	const showAsideFilter = () => !isCompareSupplier && !isSeeSuppliersAvailable;

	return (
		<S.Container>
			<div className={classes.root}>
				<AppBar position="sticky" classes={{ colorPrimary: classes.appBar }} className={classes.appBar}>
					<HeaderSearch
						isHide={!showSupplierParts}
						handleSearchValue={handleSearchValue}
						openFilter={openFilter}
						hasClearFilter={isResetFilters}
						displayCart={displayCart}
						handleDrawerOpen={handleDrawerOpen}
						showSupplierParts={showSupplierParts}
						hasDrawer={showAsideFilter()}
					/>
					{showAsideFilter() && (
						<Drawer
							className={classes.drawer}
							variant="persistent"
							anchor="left"
							open={openFilter}
							classes={{
								paper: classes.drawerPaper,
							}}
						>
							<AsideFilter
								selectedDamageArea={selectedDamageArea}
								selectedParts={selectedParts}
								selectedCategory={selectedCategory}
								hasScreenParts={showSupplierParts}
								handleDrawerClose={handleDrawerClose}
								handleSelectDamageArea={handleSelectDamageArea}
								handleSelectPart={handleSelectPart}
								handleClearFilters={handleClearFilters}
								handleSelectCategory={handleSelectCategory}
							/>
						</Drawer>
					)}
				</AppBar>

				<S.MainContent
					className={clsx(classes.content, {
						[classes.contentShift]: openFilter,
					})}
				>
					<S.HideComponent isHide={!showSupplierParts}>
						<ScreenPartsSupplier
							handleContinueShopping={handleContinueShopping}
							selectedDamageArea={selectedDamageArea}
							selectedParts={selectedParts}
							handleAddCart={handleAddCart}
							handleFinishOrder={handleFinishOrder}
							displayPartsDetails={displayPartsDetails}
						/>
					</S.HideComponent>
					<S.HideComponent isHide={showSupplierParts}>
						<ScreenParts
							formFilters={formFilters}
							partsGridData={partsGridData}
							handleAddCart={handleAddCart}
							setPartsGridData={setPartsGridData}
							displaySupplierParts={displaySupplierParts}
							displayCart={displayCart}
						/>
					</S.HideComponent>
				</S.MainContent>
			</div>

			<KogModalSideComponent
				open={openModalDetails}
				closeModal={() => {
					setOpenModalDetails(false);
					setDetailSupplier(INITIAL_DETAIL_SUPPLIER);
				}}
			>
				{openModalDetails && (
					<ModalPartDetails
						closeModal={() => {
							setOpenModalDetails(false);
							setDetailSupplier(INITIAL_DETAIL_SUPPLIER);
						}}
						guid={detailSupplier.guid}
						quoteSupplierItem={detailSupplier.supplierInventoryGuid}
					/>
				)}
			</KogModalSideComponent>
			<KogModalSideComponent
				open={openModalCart}
				closeModal={() => {
					setOpenModalCart(false);
				}}
			>
				<ModalCart handleFinishOrder={handleFinishOrder} handleAddCart={handleAddCart} />
			</KogModalSideComponent>
		</S.Container>
	);
}

export default StepTwoScreen;
